import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import { Card, CardContent } from "@veil/ui/components/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@veil/ui/components/dropdown-menu";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@veil/ui/components/empty";
import { Skeleton } from "@veil/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@veil/ui/components/tooltip";
import { formatBytes, formatRelative } from "@veil/ui/lib/format";
import {
	FileArchive,
	Loader2,
	MoreHorizontal,
	RefreshCcw,
	Trash2,
} from "lucide-react";
import { useState } from "react";
import { hasRole, useAuth } from "@/lib/auth-context";
import { DeleteUploadDialog } from "./delete-upload-dialog";
import type { RawBundle } from "./types";
import {
	useBundles,
	useDeleteUpload,
	useReinspectAll,
	useReinspectOne,
} from "./use-uploads";

export function UploadsPage() {
	const { user } = useAuth();
	const canManage = hasRole(user, "operator");

	const { data, isLoading } = useBundles();
	const reinspectAll = useReinspectAll();
	const reinspectOne = useReinspectOne();
	const [pendingDelete, setPendingDelete] = useState<RawBundle | null>(null);
	const deleteUpload = useDeleteUpload({
		onDeleted: () => setPendingDelete(null),
	});

	const bundles = data?.bundles ?? [];

	return (
		<div className="space-y-6">
			<header className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Uploads</h1>
					<p className="text-sm text-muted-foreground">
						Raw profile bundles staged for publishing.
					</p>
				</div>
				{canManage ? (
					<Button
						variant="outline"
						onClick={() => reinspectAll.mutate()}
						disabled={reinspectAll.isPending}
					>
						<RefreshCcw
							className={
								reinspectAll.isPending
									? "mr-2 h-4 w-4 animate-spin"
									: "mr-2 h-4 w-4"
							}
						/>
						Re-inspect all
					</Button>
				) : null}
			</header>

			<Card>
				<CardContent className="p-0">
					{isLoading ? (
						<div className="p-6">
							<Skeleton className="h-32 w-full" />
						</div>
					) : bundles.length === 0 ? (
						<Empty className="my-6 border-0">
							<EmptyHeader>
								<EmptyMedia variant="icon">
									<FileArchive />
								</EmptyMedia>
								<EmptyTitle>No uploads yet</EmptyTitle>
								<EmptyDescription>
									Upload a raw profile bundle from the publish flow to see it
									staged here.
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Filename</TableHead>
									<TableHead>Size</TableHead>
									<TableHead className="hidden md:table-cell">Format</TableHead>
									<TableHead className="hidden lg:table-cell">
										SHA-256
									</TableHead>
									<TableHead>Uploaded</TableHead>
									<TableHead className="hidden md:table-cell">By</TableHead>
									<TableHead>Used</TableHead>
									{canManage ? (
										<TableHead className="w-12 text-right">Actions</TableHead>
									) : null}
								</TableRow>
							</TableHeader>
							<TableBody>
								{bundles.map((b) => (
									<UploadRow
										key={b.id}
										bundle={b}
										canManage={canManage}
										reinspecting={
											reinspectOne.isPending && reinspectOne.variables === b.id
										}
										onReinspect={() => reinspectOne.mutate(b.id)}
										onDelete={() => setPendingDelete(b)}
									/>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			<DeleteUploadDialog
				open={pendingDelete !== null}
				onOpenChange={(open) => {
					if (!open) setPendingDelete(null);
				}}
				bundle={pendingDelete}
				confirming={deleteUpload.isPending}
				onConfirm={() => {
					if (pendingDelete) deleteUpload.mutate(pendingDelete.id);
				}}
			/>
		</div>
	);
}

interface UploadRowProps {
	bundle: RawBundle;
	canManage: boolean;
	reinspecting: boolean;
	onReinspect: () => void;
	onDelete: () => void;
}

function UploadRow({
	bundle,
	canManage,
	reinspecting,
	onReinspect,
	onDelete,
}: UploadRowProps) {
	const consumed = Boolean(bundle.consumedById);

	return (
		<TableRow>
			<TableCell className="max-w-[28rem] truncate font-mono text-xs">
				{bundle.filename}
			</TableCell>
			<TableCell className="tabular-nums">
				{formatBytes(bundle.sizeBytes)}
			</TableCell>
			<TableCell className="hidden md:table-cell">
				<FormatBadge bundle={bundle} />
			</TableCell>
			<TableCell className="hidden font-mono text-xs lg:table-cell">
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="cursor-help text-muted-foreground hover:text-foreground">
							{bundle.sha256.slice(0, 12)}…
						</span>
					</TooltipTrigger>
					<TooltipContent className="font-mono text-xs">
						{bundle.sha256}
					</TooltipContent>
				</Tooltip>
			</TableCell>
			<TableCell className="text-muted-foreground">
				{formatRelative(bundle.uploadedAt)}
			</TableCell>
			<TableCell className="hidden md:table-cell">
				{bundle.uploadedBy ?? <span className="text-muted-foreground">—</span>}
			</TableCell>
			<TableCell>
				{consumed ? (
					<Badge variant="success">used</Badge>
				) : (
					<Badge variant="outline">unused</Badge>
				)}
			</TableCell>
			{canManage ? (
				<TableCell className="text-right">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon-sm"
								aria-label={`Actions for ${bundle.filename}`}
							>
								{reinspecting ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<MoreHorizontal className="size-4" />
								)}
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-44">
							<DropdownMenuItem
								onSelect={() => onReinspect()}
								disabled={reinspecting}
							>
								<RefreshCcw className="size-4" />
								Re-inspect
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								variant="destructive"
								disabled={consumed}
								onSelect={() => onDelete()}
							>
								<Trash2 className="size-4" />
								Delete
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</TableCell>
			) : null}
		</TableRow>
	);
}

function FormatBadge({ bundle }: { bundle: RawBundle }) {
	if (bundle.inspectionError) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<Badge variant="destructive" className="cursor-help">
						error
					</Badge>
				</TooltipTrigger>
				<TooltipContent className="max-w-sm break-words">
					{bundle.inspectionError}
				</TooltipContent>
			</Tooltip>
		);
	}
	if (bundle.detectedFormat) {
		return <Badge variant="secondary">{bundle.detectedFormat}</Badge>;
	}
	return <span className="text-xs text-muted-foreground">—</span>;
}
